import { useMemo } from "react";

import App from "byzantine/src/App";
import { entities, useSidebarMenu } from "byzantine";

import type { NAFOption, SidebarOptionItem } from "../sidebar.types";
import type { LegacyNafUrl } from "types";

export const toLegacyNafOption = (link: LegacyNafUrl): NAFOption => ({
  discriminator: "legacy-naf",
  title: link.title,
  url: link.url,
  open_new_tab: link.open_new_tab ?? true, // default to true for legacy links
});

/**
 * Hook that computes NAF sub-items for menu groups that show NAF apps
 * (e.g., Tools).
 *
 * Internalizes `useNAFItems` and `useSidebarMenu` to derive the set
 * of claimed top-level NAF client IDs and filter out money-movement
 * NAF apps (those appear under pay-and-transfer).
 */
export const useNAFOptions = (
  legacyNafUrls: LegacyNafUrl[] | undefined
): SidebarOptionItem[] => {
  const appItems = entities.institution.useNAFItems();
  const sidebarMenu = useSidebarMenu();

  return useMemo(() => {
    const claimedNafClientIds = new Set(
      sidebarMenu
        .filter((item) => item.nafClientId)
        .map((item) => item.nafClientId as string)
    );
    const hasTransferMenu = sidebarMenu.some(
      (item) => item.key === "pay-and-transfer"
    );

    const appNafOptions: NAFOption[] = Object.entries(appItems)
      .map(([_name, { app }]): NAFOption | null => {
        if (!app) return null;

        // Money-movement NAF apps are surfaced under pay-and-transfer, so we
        // exclude anything flagged is_money_movement from the general NAF list.
        if (app.is_money_movement) return null;

        const clientId = app.client_id as string | undefined;
        if (clientId && claimedNafClientIds.has(clientId)) return null;

        try {
          const deserializedApp = App.deserialize(app);
          return { discriminator: "app", app: deserializedApp };
        } catch {
          return null;
        }
      })
      .filter((item): item is NAFOption => item !== null);

    const legacyNafOptions: NAFOption[] = (() => {
      if (!legacyNafUrls) return [];

      const deduped = legacyNafUrls.reduce<Record<string, LegacyNafUrl>>(
        (acc, link) => ({ ...acc, [link.title]: link }),
        {}
      );

      return Object.values(deduped)
        .filter((link) => !(hasTransferMenu && link.is_money_movement))
        .map(toLegacyNafOption);
    })();

    return [...appNafOptions, ...legacyNafOptions].map(
      (nafOption): SidebarOptionItem => ({
        type: "naf",
        nafOption,
      })
    );
  }, [appItems, legacyNafUrls, sidebarMenu]);
};
